import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatMoney, formatTRY } from "@/lib/format";

export default function PODeleteDialog({ po, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [chk, setChk] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/purchase-orders/${po.id}/delete-check`).then(({ data }) => setChk(data)).catch((e) => toast.error(apiError(e))); }, [po.id]);
  const canCancel = (po.next_statuses || []).includes("iptal");
  const closeRest = async () => {
    setBusy(true);
    try {
      for (const l of chk.open_lines) await api.post(`/purchase-orders/${po.id}/lines/${l.line_id}/close`, { reason });
      toast.success(`${po.po_no}: ${chk.open_lines.length} açık kalem kapatıldı`); onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/delete`, { reason });
      toast.success(`${data.po_no} silindi${data.removed_drafts.length ? ` · taslak ürün kaldırıldı: ${data.removed_drafts.join(", ")}` : ""}`); onDone();
    } catch (e) { toast.error(apiError(e)); const dt = e?.response?.data?.detail; if (dt?.blockers) setChk({ ...chk, blockers: dt.blockers, suggest: dt.suggest }); }
    setBusy(false);
  };
  const cancel = async () => {
    try { await api.post(`/purchase-orders/${po.id}/status`, { status: "iptal", note: reason || null }); toast.success(`${po.po_no} iptal edildi`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  const blocked = chk?.blockers?.length > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="po-delete-dialog">
        <DialogHeader><DialogTitle>Siparişi Sil · {po.po_no}</DialogTitle>
          <DialogDescription>Yumuşak silme: kayıt ve denetim geçmişi saklanır; listelerden, aramadan ve raporlardan kaldırılır.</DialogDescription></DialogHeader>
        <div className="space-y-2 text-[13px]">
          <div className="grid grid-cols-3 gap-2 rounded border p-2">
            <div><div className="text-[11px] text-slate-400">Cari</div><div className="truncate" data-testid="po-delete-cari">{po.supplier_name}</div></div>
            <div><div className="text-[11px] text-slate-400">Toplam</div><div className="font-mono" data-testid="po-delete-total">{formatMoney(po.total, po.currency)}</div></div>
            <div><div className="text-[11px] text-slate-400">TL</div><div className="font-mono">{formatTRY(po.total_try)}</div></div>
          </div>
          {!chk ? <Loader2 className="h-4 w-4 animate-spin" /> : blocked ? (
            <div className="rounded border border-rose-300 bg-rose-50 p-2 text-rose-800" data-testid="po-delete-blockers">
              <b>Silinemez:</b><ul className="list-disc pl-5">{chk.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              {chk.suggest === "kalani_kapat"
                ? <div className="mt-1" data-testid="po-delete-suggest">Mal kabul yapılmış sipariş silinemez ve iptal edilemez. Bunun yerine açık kalemleri <b>Kalanı Kapat</b> ile kapatın{chk.open_lines?.length ? ` (${chk.open_lines.length} açık kalem)` : " (açık kalem yok)"}.</div>
                : <div className="mt-1" data-testid="po-delete-suggest">Bunun yerine siparişi <b>İptal Et</b> kullanın{canCancel ? "" : " (bu durumda iptal de mümkün değil)"}.</div>}
            </div>
          ) : chk.drafts.length > 0 && <div className="rounded bg-amber-50 p-2 text-amber-900" data-testid="po-delete-drafts">Yalnız bu sipariş için açılmış taslak ürün kartları silinecek: <span className="font-mono">{chk.drafts.join(", ")}</span>. Kod–GTIN çiftleri yeniden verilmez (emekli kalır).</div>}
          <label className="block">Gerekçe *<textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full rounded border p-2" data-testid="po-delete-reason" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          {blocked && chk.suggest === "iptal" && canCancel && <Button variant="outline" className="text-rose-700" onClick={cancel} data-testid="po-delete-cancel-instead">İptal Et</Button>}
          {blocked && chk.suggest === "kalani_kapat" && chk.open_lines?.length > 0 && <Button variant="outline" className="text-rose-700" disabled={busy || reason.trim().length < 5} onClick={closeRest} title="Gerekçe alanı kapatma gerekçesi olarak kullanılır" data-testid="po-delete-close-rest">Kalanı Kapat</Button>}
          <Button disabled={busy || !chk || blocked || reason.trim().length < 5} onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid="po-delete-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}Sil</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
